import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { SkeletonComponent } from '../shared/skeleton/skeleton.component';

export interface FilterItem {
  title: string;
  count: number;
  id?: number; // Category ID for filtering
  selected?: boolean; // Track selection state
}

export interface SalaryPeriod {
  id: number;
  periodKey: string;
  sortOrder: number;
}

export interface FilterCard {
  icon: string;
  title: string;
  items: FilterItem[];
  footerText?: string;
  footerLink?: string; // Link for footer button (e.g., '/companies')
  type: 'items' | 'salary' | 'checkboxes' | 'companies';
  expanded?: boolean; // Track expanded state
  salaryPeriods?: SalaryPeriod[]; // Available salary periods for salary type
  maxItems?: number; // Max items to show before "show more"
}

@Component({
  selector: 'app-filter-cards',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, TranslocoModule, SkeletonComponent],
  templateUrl: './filter-cards.component.html',
  styleUrls: ['./filter-cards.component.scss']
})
export class FilterCardsComponent implements OnChanges {
  @Input() filterCards: FilterCard[] = [];
  @Input() salaryPeriods: SalaryPeriod[] = [];
  @Input() loading: boolean = false;
  // Currently-applied salary filter (from the URL / filter state), so the card
  // reflects what is actually filtering rather than its own stale defaults.
  @Input() salaryFilter: { minSalary: number; maxSalary: number; periodKey: string } | null = null;
  @Output() filterSelected = new EventEmitter<{ cardTitle: string; item: FilterItem }>();
  @Output() salaryFilterChanged = new EventEmitter<{ min: number; max: number; periodKey: string }>();

  // null = unset → the input shows its placeholder instead of a value that
  // looks selected.
  salaryRange: { min: number | null; max: number | null } = { min: null, max: null };
  selectedSalaryPeriodKey = 'month'; // Default to month

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['salaryFilter'] && this.salaryFilter) {
      const incomingMin = this.salaryFilter.minSalary > 0 ? this.salaryFilter.minSalary : null;
      const incomingMax = this.salaryFilter.maxSalary > 0 ? this.salaryFilter.maxSalary : null;
      const incomingPeriod = this.salaryFilter.periodKey || 'month';
      // Only adopt external changes (URL restore, clear-all). Skip the echo of
      // the user's own edits so we don't disturb in-progress typing.
      if (
        incomingMin !== this.salaryRange.min ||
        incomingMax !== this.salaryRange.max ||
        incomingPeriod !== this.selectedSalaryPeriodKey
      ) {
        this.salaryRange = { min: incomingMin, max: incomingMax };
        this.selectedSalaryPeriodKey = incomingPeriod;
      }
    }
  }

  setSalaryPeriod(periodKey: string): void {
    this.selectedSalaryPeriodKey = periodKey;
    this.emitSalaryFilter();
  }

  onSalaryChange(): void {
    this.emitSalaryFilter();
  }

  // Keep <option> elements stable when salaryPeriods re-emits with a new array
  // reference (filter reload). Without this the native select rebuilds its
  // options, loses the selected value, and snaps back to the first option.
  trackByPeriodKey(_index: number, period: SalaryPeriod): string {
    return period.periodKey;
  }

  private emitSalaryFilter(): void {
    const minSalary = Number(this.salaryRange.min) || 0;
    const maxSalary = Number(this.salaryRange.max) || 0;

    // Always emit, but the service will handle whether to send to backend
    // based on whether min or max has a value
    this.salaryFilterChanged.emit({
      min: minSalary,
      max: maxSalary,
      periodKey: this.selectedSalaryPeriodKey
    });
  }

  toggleFilterItem(card: FilterCard, item: FilterItem): void {
    // Toggle selection state
    item.selected = !item.selected;
    this.filterSelected.emit({ cardTitle: card.title, item });
  }

  toggleShowMore(card: FilterCard): void {
    card.expanded = !card.expanded;
  }

  getVisibleItems(card: FilterCard): FilterItem[] {
    if (card.expanded || card.items.length <= 5) {
      return card.items;
    }
    return card.items.slice(0, 5);
  }

  shouldShowToggle(card: FilterCard): boolean {
    return card.items.length > 5;
  }
}
